import React, { useState, useEffect, useCallback, useRef } from 'react';
import gateApi from '../api';
import { TransactionRow } from '../components/UI';

// Yard-stage filters, in process order, each with the dot color it shows as
// a status badge elsewhere in the app (see constants.js STATUS_CONFIG) so the
// pill and the rows underneath it read as the same color language.
// NEEDS_CHALLAN / NEEDS_PAYMENT are virtual statuses handled server-side —
// both share the real lifecycleStatus PENDING_CHALLAN, split by whether a
// Challan row exists yet (see TransactionController).
const FILTERS = [
  { key: 'PENDING_TARE_WEIGHT', label: 'Pending Tare',    dot: '#0C447C' },
  { key: 'PENDING_LOADING',     label: 'Pending Gross',   dot: '#633806' },
  { key: 'NEEDS_CHALLAN',       label: 'Pending Challan', dot: '#5B21B6' },
  { key: 'NEEDS_PAYMENT',       label: 'Pending Payment', dot: '#9D174D' },
  { key: 'PENDING_EXIT',        label: 'Pending Exit',    dot: '#7A4B06' },
  { key: 'COMPLETED',           label: 'Completed',       dot: '#27500A' },
  { key: 'REJECTED',            label: 'Rejected',        dot: '#791F1F' },
];
// Rows under both billing tabs share lifecycleStatus PENDING_CHALLAN, so their
// badge would otherwise read the same generic label in both. Override it to
// match the queue that produced the row. These two tabs deliberately overlap:
// a vehicle with neither step done appears in both, because both counters need
// to act on it (see YardTransactionRepository).
const STATUS_OVERRIDE = {
  NEEDS_CHALLAN: 'NEEDS_CHALLAN',
  NEEDS_PAYMENT: 'NEEDS_PAYMENT',
};
const PAGE_SIZE = 50;

export default function Transactions() {
  // No "All" tab — an unbounded browse of every transaction ever isn't useful
  // operationally; land on the first pipeline stage instead.
  const [filter, setFilter]       = useState(FILTERS[0].key);
  const [search, setSearch]       = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage]           = useState(0);
  const [data, setData]           = useState({ items: [], totalElements: 0, totalPages: 1 });
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState('');

  // debounce the search box (and jump back to page 0 on a new search)
  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 350);
    return () => clearTimeout(id);
  }, [search]);

  // reset to first page when the filter changes
  useEffect(() => { setPage(0); }, [filter]);

  // Guards against out-of-order responses: switching tabs fast (e.g. Pending
  // Tare -> Pending Challan before the first request lands) fires two requests
  // with no guarantee they resolve in order. Without this, a slow response for
  // a tab you've already left can land after the new tab's response and
  // silently overwrite it with stale data. Each call to load() gets a ticket;
  // a response only gets applied if no newer call has started since.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const myRequestId = ++requestId.current;
    setLoading(true); setErr('');
    try {
      const res = await gateApi.getTransactions({
        page, size: PAGE_SIZE, status: filter, search: debounced,
      });
      if (myRequestId !== requestId.current) return; // superseded by a newer tab/page/search
      if (res?.items) {
        setData(res);
      } else if (Array.isArray(res)) {
        // older backend returned a plain array — show it as a single page
        setData({ items: res, totalElements: res.length, totalPages: 1 });
      }
    } catch (e) {
      if (myRequestId !== requestId.current) return;
      // Clear the list rather than leaving the previous tab's rows on screen —
      // silently keeping stale data made a failing request look like this tab
      // legitimately contained another tab's vehicles.
      setData({ items: [], totalElements: 0, totalPages: 1 });
      setErr(e.response?.data?.message || e.response?.data?.error || 'Could not load transactions.');
    } finally {
      if (myRequestId === requestId.current) setLoading(false);
    }
  }, [page, filter, debounced]);

  useEffect(() => { load(); }, [load]);

  const { items, totalElements, totalPages } = data;
  const canPrev = page > 0;
  const canNext = page < (totalPages - 1);

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Search */}
      <input
        type="text"
        placeholder="Search plate number..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '11px 14px',
          border: '1px solid var(--border)', borderRadius: 10, fontSize: 14,
          background: '#fff', color: 'var(--text-primary)', outline: 'none',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.05em',
        }}
      />

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
        {FILTERS.map(f => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 14px', borderRadius: 20,
                background: active ? '#2C2C2A' : '#fff',
                color: active ? '#fff' : 'var(--text-muted)',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                border: active ? '1px solid #2C2C2A' : '1px solid var(--border)',
                transition: 'all 0.15s',
              }}
            >
              {f.dot && (
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: f.dot, opacity: active ? 1 : 0.85,
                }} />
              )}
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Count */}
      <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingLeft: 2 }}>
        {loading ? 'Loading…' : err ? '—' : `${totalElements} record${totalElements === 1 ? '' : 's'}`}
        {!err && totalPages > 1 && ` · page ${page + 1} of ${totalPages}`}
      </div>

      {err && (
        <div style={{
          background: '#FCEBEB', border: '1px solid #F7C1C1', color: '#791F1F',
          borderRadius: 12, padding: '12px 16px', fontSize: 13,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}>
          <span>{err}</span>
          <button onClick={load} style={{
            flexShrink: 0, padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
            border: '1px solid #F7C1C1', background: '#fff', color: '#791F1F',
            fontSize: 12, fontWeight: 700,
          }}>Retry</button>
        </div>
      )}

      {/* List */}
      <div style={{
        background: '#fff', border: '1px solid var(--border)',
        borderRadius: 14, overflow: 'hidden',
      }}>
        {items.length === 0 ? (
          <div style={{ padding: '36px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {loading ? 'Loading…' : err ? 'Could not load this tab' : 'No transactions found'}
          </div>
        ) : (
          items.map(tx => (
            <TransactionRow key={tx.transactionId} tx={tx} statusOverride={STATUS_OVERRIDE[filter]} />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <button
            onClick={() => canPrev && setPage(p => p - 1)}
            disabled={!canPrev}
            style={pageBtn(canPrev)}
          >← Prev</button>

          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {page + 1} / {totalPages}
          </span>

          <button
            onClick={() => canNext && setPage(p => p + 1)}
            disabled={!canNext}
            style={pageBtn(canNext)}
          >Next →</button>
        </div>
      )}
    </div>
  );
}

function pageBtn(enabled) {
  return {
    padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    background: enabled ? '#fff' : '#F5F4F0',
    color: enabled ? 'var(--text-primary)' : 'var(--text-muted)',
    border: '1px solid var(--border)',
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}
